import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, LogOut, ArrowLeft, RefreshCw, Inbox } from "lucide-react";
import { API, getToken, setToken, clearToken, withAuth } from "../lib/api";
import { LogoMark } from "../components/Logo";

const statusStyles = {
  new: "bg-[#FFFFFF] text-black",
  contacted: "bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/40",
  closed: "bg-white/10 text-[#9CA3AF] border border-white/10",
};

const inputCls =
  "w-full border border-[#2E333D] bg-[#0B0C0E] px-4 py-3.5 text-sm text-[#F3F4F6] placeholder-[#6B7280] outline-none transition-colors focus:border-[#FFFFFF]";

const GoogleGlyph = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
  </svg>
);

export default function Admin() {
  const location = useLocation();
  const [token, setTokenState] = useState(getToken());
  const [sessionUser, setSessionUser] = useState(location.state?.user || null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.authError || "");
  const [loading, setLoading] = useState(false);
  const [inquiries, setInquiries] = useState(null);

  const authed = Boolean(token || sessionUser);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/inquiries`, withAuth());
      setInquiries(data);
    } catch (e) {
      if (e.response?.status === 401) {
        clearToken();
        setTokenState(null);
        setSessionUser(null);
      } else {
        toast.error("Could not load enquiries.");
      }
    }
  };

  useEffect(() => {
    if (authed) {
      load();
      return;
    }
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setSessionUser(data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      setToken(data.access_token);
      setTokenState(data.access_token);
      toast.success(`Welcome back, ${data.user.name}.`);
    } catch (e2) {
      const detail = e2.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    clearToken();
    setTokenState(null);
    setSessionUser(null);
    setInquiries(null);
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/inquiries/${id}`, { status }, withAuth());
      setInquiries((list) => list.map((i) => (i.id === id ? { ...i, status } : i)));
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0C0E] px-5 py-10 text-[#F3F4F6] sm:px-8">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="admin-back-link" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#9CA3AF] hover:text-[#FFFFFF]">
            <ArrowLeft size={14} /> Back to site
          </Link>
          <div className="flex items-center gap-3">
            <LogoMark size={30} />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#9CA3AF]">Admin Portal</span>
          </div>
        </div>

        {!authed ? (
          <div
            data-testid="admin-login-modal"
            className="mx-auto mt-20 max-w-md border border-[#2E333D] bg-[#1A1D24] p-8 sm:p-10"
          >
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFFFFF]">Restricted</p>
            <h1 className="mt-3 font-display text-3xl font-black uppercase tracking-tight">Admin Login</h1>

            <button
              onClick={googleSignIn}
              data-testid="admin-google-signin-button"
              className="mt-8 flex w-full items-center justify-center gap-3 border border-white/25 bg-white/5 px-8 py-4 font-mono text-sm uppercase tracking-[0.1em] text-[#F3F4F6] transition-colors hover:border-white hover:bg-white hover:text-black"
            >
              <GoogleGlyph /> Sign in with Google
            </button>
            <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B7280]">
              Owner account only — gativan55@gmail.com
            </p>

            <div className="my-6 flex items-center gap-4">
              <span className="h-px flex-1 bg-[#2E333D]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">or</span>
              <span className="h-px flex-1 bg-[#2E333D]" />
            </div>

            <form onSubmit={login} className="space-y-5">
              <input
                data-testid="admin-login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Admin email"
                className={inputCls}
              />
              <input
                data-testid="admin-login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className={inputCls}
              />
              {error && (
                <p data-testid="admin-login-error" className="font-mono text-xs uppercase tracking-[0.1em] text-[#EF4444]">
                  {error}
                </p>
              )}
              <button
                type="submit"
                data-testid="admin-login-submit"
                disabled={loading}
                className="w-full bg-[#FFFFFF] px-8 py-4 font-mono text-sm font-semibold uppercase tracking-[0.15em] text-black transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              >
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>
          </div>
        ) : (
          <div className="mt-12">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFFFFF]">Project enquiries</p>
                <h1 className="mt-2 font-display text-3xl font-black uppercase tracking-tight sm:text-4xl">Inbox</h1>
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="admin-refresh-button"
                  onClick={load}
                  className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-[#9CA3AF] hover:border-[#FFFFFF] hover:text-[#FFFFFF]"
                >
                  <RefreshCw size={13} /> Refresh
                </button>
                <button
                  data-testid="admin-logout-button"
                  onClick={logout}
                  className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-[#9CA3AF] hover:border-[#EF4444] hover:text-[#EF4444]"
                >
                  <LogOut size={13} /> Logout
                </button>
              </div>
            </div>

            {inquiries === null ? (
              <p className="mt-16 text-center font-mono text-xs uppercase tracking-[0.25em] text-[#9CA3AF]">Loading…</p>
            ) : inquiries.length === 0 ? (
              <div data-testid="admin-inquiry-list" className="mt-10 border border-dashed border-[#2E333D] p-16 text-center">
                <Inbox size={32} className="mx-auto text-[#6B7280]" />
                <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-[#9CA3AF]">No enquiries yet</p>
              </div>
            ) : (
              <div data-testid="admin-inquiry-list" className="mt-10 space-y-4">
                {inquiries.map((inq) => (
                  <article key={inq.id} data-testid={`admin-inquiry-${inq.id}`} className="border border-[#2E333D] bg-[#1A1D24] p-6">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <h3 className="font-display text-xl font-bold uppercase tracking-tight">{inq.name}</h3>
                        <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">
                          {inq.service} · {new Date(inq.created_at).toLocaleString("en-IN")}
                        </p>
                      </div>
                      <span className={`px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${statusStyles[inq.status] || statusStyles.new}`}>
                        {inq.status}
                      </span>
                    </div>
                    <p className="mt-4 text-sm leading-relaxed text-[#9CA3AF]">{inq.message}</p>
                    <div className="mt-5 flex flex-wrap items-center gap-3">
                      <a href={`tel:${inq.phone.replace(/\s/g, "")}`} data-testid={`admin-call-${inq.id}`} className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.1em] text-[#F3F4F6] hover:border-[#FFFFFF] hover:text-[#FFFFFF]">
                        <Phone size={13} /> {inq.phone}
                      </a>
                      {inq.email && (
                        <a href={`mailto:${inq.email}`} className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.1em] text-[#F3F4F6] hover:border-[#FFFFFF] hover:text-[#FFFFFF]">
                          <Mail size={13} /> {inq.email}
                        </a>
                      )}
                      <select
                        data-testid={`admin-status-${inq.id}`}
                        value={inq.status}
                        onChange={(e) => setStatus(inq.id, e.target.value)}
                        className="ml-auto border border-[#2E333D] bg-[#0B0C0E] px-3 py-2 font-mono text-xs uppercase tracking-[0.1em] text-[#9CA3AF] outline-none focus:border-[#FFFFFF]"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
